/* eslint-disable react/require-default-props */
import React from "react";

type PaginationProps = {
  page: number;
  totalPages: number;
  // eslint-disable-next-line no-unused-vars
  onPageChange: (page: number) => void;
  /**
   * "dark" is the always-dark calls page; "app" follows an AppLayout page's
   * light/dark theme with the home page's Button styling.
   */
  tone?: "dark" | "app";
  /** Number buttons in place of the "Page X of Y" text. */
  numbered?: boolean;
  label?: string;
  previousLabel?: string;
  nextLabel?: string;
};

type PageItem = number | "gap";

/** Pages shown either side of the current one in numbered mode. */
const WINDOW_RADIUS = 2;

const TONES = {
  dark: {
    button:
      "border border-gray-700 bg-gray-800 text-gray-100 hover:bg-gray-700",
    current: "border border-indigo-500 bg-indigo-600 text-white",
    text: "text-gray-400",
  },
  app: {
    // tailwind.css: safe from Bootstrap's !important utilities on legacy pages.
    button: "app-control",
    current: "app-control-current",
    text: "text-gray-500 dark:text-gray-400",
  },
};

const baseButtonClasses =
  "px-3 py-1.5 rounded-md text-sm " +
  "disabled:opacity-40 disabled:cursor-not-allowed";

/**
 * The first and last page, plus WINDOW_RADIUS either side of the current one.
 * A gap of exactly one page shows that page rather than an ellipsis, which
 * would take the same room and hide where it leads.
 */
export function pageWindow(page: number, totalPages: number): PageItem[] {
  const shown = new Set<number>([1, totalPages]);

  for (
    let candidate = page - WINDOW_RADIUS;
    candidate <= page + WINDOW_RADIUS;
    candidate += 1
  ) {
    if (candidate >= 1 && candidate <= totalPages) shown.add(candidate);
  }

  const sorted = Array.from(shown).sort((a, b) => a - b);
  const items: PageItem[] = [];

  sorted.forEach((current, index) => {
    const previous = sorted[index - 1];

    if (previous !== undefined && current - previous === 2) {
      items.push(previous + 1);
    } else if (previous !== undefined && current - previous > 2) {
      items.push("gap");
    }

    items.push(current);
  });

  return items;
}

export default function Pagination({
  page,
  totalPages,
  onPageChange,
  tone = "dark",
  numbered = false,
  label = "Pagination",
  previousLabel = "Previous",
  nextLabel = "Next",
}: PaginationProps) {
  if (totalPages <= 1) return null;

  const colors = TONES[tone];
  const buttonClasses = `${baseButtonClasses} ${colors.button}`;

  return (
    <nav
      aria-label={label}
      className="flex items-center justify-between gap-4 pt-4"
    >
      <button
        type="button"
        className={buttonClasses}
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
      >
        {previousLabel}
      </button>

      {numbered ? (
        <ul className="flex flex-wrap items-center justify-center gap-1">
          {pageWindow(page, totalPages).map((item, index) =>
            item === "gap" ? (
              // Two gaps can appear, so the key needs the position.
              // eslint-disable-next-line react/no-array-index-key
              <li key={`gap-${index}`} className={`px-1 ${colors.text}`}>
                …
              </li>
            ) : (
              <li key={item}>
                <button
                  type="button"
                  aria-current={item === page ? "page" : undefined}
                  className={`${baseButtonClasses} ${
                    item === page ? colors.current : colors.button
                  }`}
                  onClick={() => {
                    if (item !== page) onPageChange(item);
                  }}
                >
                  {item}
                </button>
              </li>
            ),
          )}
        </ul>
      ) : (
        <span className={`text-sm ${colors.text}`}>
          Page {page} of {totalPages}
        </span>
      )}

      <button
        type="button"
        className={buttonClasses}
        disabled={page >= totalPages}
        onClick={() => onPageChange(page + 1)}
      >
        {nextLabel}
      </button>
    </nav>
  );
}
